'use client'

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RotateCcw, Home } from 'lucide-react'
import { reportError } from '@/lib/error-report'
import { t } from '@/lib/i18n'

/**
 * The net under a render error.
 *
 * There was none. A single exception thrown while rendering unmounts React's
 * whole tree, and the till was left with a black screen — no message, no
 * button, nothing to press. The owner hit exactly that opening a waybill
 * (2026-09-12, my own bug: a memo calling a `const` arrow function still in
 * its temporal dead zone) and described it as the app freezing, which is
 * precisely what it looks like from the other side.
 *
 * At a till this is the worst failure there is, and it must not be silent.
 * The screen says what happened, says the thing a shopkeeper actually needs
 * to know — the sales already made are safe — and offers a way out that does
 * not require knowing what a browser refresh is.
 *
 * `reset` re-mounts the subtree without a reload, which is enough when the
 * crash was in one dialog and the rest of the app is fine. A reload is the
 * second button, not the first: reloading loses the cart.
 */
type Props = {
  children: ReactNode
  /** Where this boundary sits, for the console line. */
  label?: string
  /** Shown instead of the default screen (e.g. a smaller inline one). */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

type State = { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A shop owner will not open the console. Keep it whole there, and since
    // 111 send it home too — the morning digest counts crashes per shop.
    console.error(`[ErrorBoundary${this.props.label ? `: ${this.props.label}` : ''}]`, error, info.componentStack)
    reportError('render', error,
      `${this.props.label ? `[${this.props.label}] ` : ''}${error.stack ?? ''}\n--- component stack ---${info.componentStack ?? ''}`)
  }

  // A deploy removes the previous build's chunks. A window still running the
  // old build then fails to import a page (owner, 2026-09-18) — re-rendering
  // asks for the same missing file again, so for THAT error „თავიდან ცდა"
  // reloads the page and picks up the new build.
  reset = () => {
    const m = String(this.state.error?.message ?? '')
    if (/dynamically imported module|Importing a module script failed|error loading dynamically/i.test(m)) {
      window.location.reload()
      return
    }
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(error, this.reset)

    return (
      <div className="flex min-h-[60dvh] w-full items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[15px] font-black text-foreground">{t('error.title')}</p>
              <p className="mt-1 text-[12px] text-muted-foreground leading-relaxed">
                {t('error.body')}
              </p>
            </div>
          </div>

          {/* The message itself, not a prettier version of it: it is the only
              thing that makes a screenshot worth sending. */}
          <pre className="max-h-32 overflow-auto rounded-xl bg-muted p-3 text-[12px] leading-relaxed font-mono whitespace-pre-wrap break-words text-muted-foreground">
            {error.message || String(error)}
          </pre>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="press h-10 px-4 rounded-xl bg-primary text-primary-foreground text-[13px] font-black inline-flex items-center gap-1.5 hover:bg-primary/90 transition-colors"
            >
              <RotateCcw className="size-4" /> {t('error.retry')}
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = '/pos' }}
              className="h-10 px-4 rounded-xl border border-border text-[13px] font-bold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors inline-flex items-center gap-1.5"
            >
              <Home className="size-4" /> {t('error.toPos')}
            </button>
          </div>
        </div>
      </div>
    )
  }
}
